import type {
  ReviewQueueResponse,
  ReconciliationMatchDTO,
  MatchedObjectType,
  ClientRuleDTO,
  EngineRunResult,
} from '@eriko/shared-types';
import { apiClient } from '../lib/api';
import { ENDPOINTS } from './endpoints';

// NOTE: every function below returns `data`, not `data.data`. The shared
// admin apiClient response interceptor (apps/admin/src/lib/api.ts) already
// unwraps the `{ success, data }` envelope — chaining a second `.data`
// access produces `undefined` for every query (was the cause of the
// "Failed to load reconciliation queue / data is undefined" admin bug).

export type QueueGroup =
  | 'all'
  | 'unmatched'
  | 'suggested'
  | 'auto'
  | 'missing-doc'
  | 'docs-without-payment'
  | 'duplicates'
  | 'transfers'
  | 'ambiguous-vendor'
  | 'amount-mismatch'
  | 'multiple-candidates';

export async function getReconciliationQueue(
  orgId: string,
  group: QueueGroup = 'all',
): Promise<ReviewQueueResponse> {
  const { data } = await apiClient.get(ENDPOINTS.adminReconciliation.queue(orgId), {
    params: { group },
  });
  return data;
}

export type RunScope = 'monthly-close' | 'transaction' | 'org-since';

export interface RunReconciliationArgs {
  scope: RunScope;
  monthlyCloseId?: string;
  transactionId?: string;
  since?: string;
}

export async function runReconciliation(
  orgId: string,
  args: RunReconciliationArgs,
): Promise<EngineRunResult> {
  // Body still requires organizationId per R1 Zod schema; path orgId is authoritative
  // but we send the same value in body for backward compatibility.
  const body: Record<string, unknown> = { organizationId: orgId, ...args };
  const { data } = await apiClient.post(ENDPOINTS.adminReconciliation.run(orgId), body);
  return data;
}

export async function getTransactionCandidates(
  orgId: string,
  transactionId: string,
): Promise<{ matches: ReconciliationMatchDTO[]; flags: unknown[] }> {
  const { data } = await apiClient.get(
    ENDPOINTS.adminReconciliation.txCandidates(orgId, transactionId),
  );
  return data;
}

export async function getMatchDetail(
  orgId: string,
  matchId: string,
): Promise<ReconciliationMatchDTO> {
  const { data } = await apiClient.get(ENDPOINTS.adminReconciliation.match(orgId, matchId));
  return data;
}

export interface CreateMatchBody {
  transactionId: string;
  matchedObjectType: MatchedObjectType;
  matchedObjectId?: string;
  allocations?: Array<{ objectType: MatchedObjectType; objectId: string; amount: string }>;
  notes?: string;
}

export async function createManualMatch(
  orgId: string,
  body: CreateMatchBody,
): Promise<ReconciliationMatchDTO> {
  const { data } = await apiClient.post(ENDPOINTS.adminReconciliation.matches(orgId), body);
  return data;
}

export async function confirmMatch(
  orgId: string,
  matchId: string,
  notes?: string,
): Promise<ReconciliationMatchDTO> {
  const { data } = await apiClient.post(
    ENDPOINTS.adminReconciliation.confirmMatch(orgId, matchId),
    { notes },
  );
  return data;
}

export async function rejectMatch(
  orgId: string,
  matchId: string,
  reason: string,
): Promise<ReconciliationMatchDTO> {
  const { data } = await apiClient.post(
    ENDPOINTS.adminReconciliation.rejectMatch(orgId, matchId),
    { reason },
  );
  return data;
}

// Mirrors the server WritebackResult union in apps/api/src/services/accounting/
// writeReconciliationPayment.ts. Repeated here to avoid a cross-app import of an
// internal server type.
export type WritebackResultDTO =
  | { kind: 'success'; provider: 'QBO'; externalId: string; entityId: string }
  | { kind: 'skipped'; reason: string };

export interface BulkConfirmResult {
  results: Array<{
    matchId: string;
    success: boolean;
    error?: string;
    // Present on each successful row: { kind: 'success' } for BILL/INVOICE that
    // posted to QBO, { kind: 'skipped' } for non-BILL/INVOICE, unmapped bank
    // account, missing connection, or unsupported provider.
    writeback?: WritebackResultDTO;
  }>;
}

export async function bulkConfirm(
  orgId: string,
  matchIds: string[],
): Promise<BulkConfirmResult> {
  const { data } = await apiClient.post(ENDPOINTS.adminReconciliation.bulkConfirm(orgId), {
    matchIds,
  });
  return data;
}

export async function listRules(orgId: string): Promise<ClientRuleDTO[]> {
  const { data } = await apiClient.get(ENDPOINTS.adminReconciliation.rules(orgId));
  return data;
}

export interface CreateRuleBody {
  ruleType:
    | 'MERCHANT_CATEGORY'
    | 'MERCHANT_ACCOUNT'
    | 'RECONCILIATION'
    | 'PATTERN'
    | 'TRANSFER'
    | 'PAYOUT';
  merchantId?: string;
  patternText?: string;
  categoryId?: string;
  accountCode?: string;
  reconciliationBehavior?: Record<string, unknown>;
  notes?: string;
  dryRun?: boolean;
}

export interface CreateRuleDryRunResponse {
  willApplyToCount: number;
  sampleTransactions: Array<{
    id: string;
    date: string;
    amount: string;
    description: string;
    rawDescription: string | null;
    type: 'DEBIT' | 'CREDIT';
    merchantName: string | null;
    currency?: string | null;
  }>;
}

export interface CreateRulePersistedResponse {
  rule: ClientRuleDTO;
  willApplyToCount: number;
}

export async function createRule(
  orgId: string,
  body: CreateRuleBody,
): Promise<CreateRuleDryRunResponse | CreateRulePersistedResponse> {
  const fullBody = { ...body, organizationId: orgId };
  const { data } = await apiClient.post(ENDPOINTS.adminReconciliation.rules(orgId), fullBody);
  return data;
}

export async function deleteRule(
  orgId: string,
  ruleId: string,
): Promise<{ deleted: boolean }> {
  const { data } = await apiClient.delete(ENDPOINTS.adminReconciliation.rule(orgId, ruleId));
  return data;
}

export async function listUnmatchedDocs(orgId: string): Promise<unknown[]> {
  const { data } = await apiClient.get(ENDPOINTS.adminReconciliation.unmatchedDocs(orgId));
  return data;
}
